// src/features/user-portfolios/components/TradeHistoryTable.tsx
//
// "Trade History (Closed Tax-Lots)" — renders one row per realized SELL event,
// straight from the backend's per-transaction Numba FIFO arrays (see
// analytics.compute_fifo_pnl / analyze_positions). Sits directly UNDER the open
// Tax-Lot Ledger grid on the portfolio dashboard. Theme-aware (light + dark) to
// match the rest of the app.

import type { ClosedLot } from '../api';

// Currency-aware money formatter via Intl.NumberFormat. ``ccy`` is the trade's
// NATIVE currency for the raw Sell Price / Fees, and the portfolio BASE currency
// for the FX-converted COGS / Realized P&L (point-in-time on the backend), so
// global codes render as CA$, £, MX$, ¥... Falls back to an ISO-prefixed number
// for exotic/unknown codes instead of throwing.
const money = (val: number, ccy: string, maxDecimals = 2) => {
    const code = (ccy || 'USD').toUpperCase();

    // FIX: Explicitly intercept GBX so the browser doesn't auto-render "GBp".
    if (code === 'GBX') {
        return `GBX ${Number(val || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: maxDecimals,
        })}`;
    }

    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: code,
            minimumFractionDigits: 2,
            maximumFractionDigits: maxDecimals,
        }).format(Number(val) || 0);
    } catch {
        return `${code} ${Number(val || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: maxDecimals,
        })}`;
    }
};

const formatPct = (val: number) => `${Number(val || 0).toFixed(2)}%`;

const getColor = (val: number) =>
    val > 0
        ? 'text-emerald-600 dark:text-emerald-400'
        : val < 0
          ? 'text-red-600 dark:text-red-400'
          : 'text-slate-500 dark:text-slate-400';

export const TradeHistoryTable = ({ history }: { history: ClosedLot[] }) => {
    if (!history || history.length === 0) {
        return (
            <div className="mt-8 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/30 p-8 text-center text-slate-500 dark:text-slate-400">
                No closed positions (Sell events) yet.
            </div>
        );
    }

    return (
        <div className="mt-8 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
            <div className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 p-4">
                <h3 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">
                    Trade History (Closed Tax-Lots)
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-500">
                    Realized P&amp;L calculated via FIFO matching algorithm.
                </p>
            </div>
            <table className="w-full text-left text-sm">
                <thead className="whitespace-nowrap border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    <tr>
                        <th className="px-4 py-3">Sell Date</th>
                        <th className="px-4 py-3">Asset</th>
                        <th className="px-4 py-3">Ccy</th>
                        <th className="px-4 py-3">Qty Sold</th>
                        <th className="px-4 py-3">Sell Price (Native)</th>
                        <th className="px-4 py-3">Sell Fees (Native)</th>
                        <th className="px-4 py-3">COGS (Base)</th>
                        <th className="px-4 py-3">Realized P&amp;L (Base)</th>
                        <th className="px-4 py-3">Return %</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                    {history.map((trade, i) => (
                        <tr
                            key={`${trade.symbol}-${trade.sell_date ?? ''}-${i}`}
                            className="transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/60"
                        >
                            <td className="whitespace-nowrap px-4 py-3 text-slate-500 dark:text-slate-400">
                                {trade.sell_date ?? '—'}
                            </td>
                            <td className="px-4 py-3 font-bold text-slate-900 dark:text-white">
                                {trade.symbol}
                            </td>
                            <td className="px-4 py-3">
                                <span className="inline-block rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                    {trade.native_currency || 'USD'}
                                </span>
                            </td>
                            <td className="px-4 py-3 font-mono text-slate-600 dark:text-slate-300">
                                {Number(trade.quantity).toLocaleString(undefined, {
                                    maximumFractionDigits:
                                        trade.instrument_type === 'CRYPTO' ? 8 : 4,
                                })}
                            </td>
                            <td className="px-4 py-3 font-mono text-slate-600 dark:text-slate-300">
                                {money(trade.sell_price_native, trade.native_currency)}
                            </td>
                            <td className="px-4 py-3 font-mono text-slate-500 dark:text-slate-500">
                                {money(trade.fees_native, trade.native_currency)}
                            </td>
                            <td className="px-4 py-3 font-mono text-slate-500 dark:text-slate-500">
                                {money(trade.cost_basis_sold_base, trade.base_currency)}
                            </td>
                            <td className="px-4 py-3 font-mono font-bold">
                                <span className={getColor(trade.realized_pnl_base)}>
                                    {trade.realized_pnl_base > 0 ? '+' : ''}
                                    {money(
                                        Math.abs(trade.realized_pnl_base),
                                        trade.base_currency,
                                    )}
                                </span>
                            </td>
                            <td className="px-4 py-3 font-mono">
                                <span
                                    className={`rounded bg-slate-100 dark:bg-slate-800 px-2 py-1 text-xs font-medium ${getColor(
                                        trade.return_pct,
                                    )}`}
                                >
                                    {trade.return_pct > 0 ? '+' : ''}
                                    {formatPct(trade.return_pct)}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default TradeHistoryTable;
